/**
 * Bandă continuă pentru orice pistă cu `data-puffy-autoscroll`.
 *
 * Pista e împachetată într-un cadru care derulează pe orizontală, cardurile se
 * dublează, iar cadrul e împins încet spre stânga. Când s-a mutat cu exact o
 * serie, poziția se dă înapoi cu o serie: conținutul se repetă la fel, deci
 * saltul cade peste pixeli identici și nu se vede. Bucla nu are capăt.
 *
 * DE CE PRIN `scrollLeft`, NU PRINTR-O ANIMAȚIE CSS DE `transform`.
 * Transformul e mai ieftin, dar mută conținutul în afara casetei de derulare,
 * iar degetul și rotița se bat cu animația. Aici omul trebuie să poată trage ca
 * să citească recenziile, deci pista rămâne un scroller adevărat, iar mișcarea
 * automată e doar cineva care împinge scroller-ul.
 *
 * DE CE SE DUBLEAZĂ CARDURILE. O bandă continuă are nevoie de destul conținut
 * cât să acopere ecranul și după ce s-a mutat cu o serie. Cu trei carduri pe un
 * ecran lat nu se poate altfel: ar rămâne gol în urma primei serii.
 *
 * DE CE NU COSTĂ DUBLU LA VIDEO. Clipurile pornesc cu `preload="none"` și sunt
 * pornite și oprite de observatorul din puffy-proof.js, care nu lasă să ruleze
 * decât ce e pe ecran. Se dublează elementele din pagină, nu decodările.
 */

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');

// Pixeli pe secundă. Se poate schimba per pistă cu
// `data-puffy-autoscroll-speed`.
const DEFAULT_SPEED = 26;

// Cât stă pe loc după ce omul a terminat de tras.
const RESUME_DELAY = 2600;

// Cât mai stau benzile oprite după ultimul eveniment de derulare verticală.
const SCROLL_QUIET = 140;

/**
 * Benzile stau pe loc cât timp pagina se derulează pe verticală.
 *
 * Banda își mișcă poziția scriind `belt.scrollLeft` la fiecare cadru, iar o
 * scriere de poziție de derulare obligă browserul să recalculeze layout-ul, pe
 * firul principal, exact acolo unde se decide dacă derularea verticală pierde
 * cadre sau nu. Două benzi înseamnă două astfel de scrieri per cadru.
 *
 * Nimeni nu citește o bandă orizontală în timp ce derulează vertical, deci nu se
 * pierde nimic: se oprește cât alunecă pagina și repornește la 140ms după ce s-a
 * oprit.
 *
 * Un singur ascultător pentru toată pagina, nu unul per bandă. Evenimentele de
 * derulare ale benzilor înseși se ignoră, altfel banda s-ar opri singură: își
 * scrie poziția, evenimentul urcă la document, iar ea se pune în pauză din cauza
 * propriei mișcări și nu mai repornește niciodată.
 */
const pageScroll = (() => {
  const belts = new Set();
  let timer = 0;
  let held = false;

  const release = () => {
    timer = 0;
    held = false;
    for (const belt of belts) belt.release();
  };

  document.addEventListener(
    'scroll',
    (event) => {
      const target = event.target;
      if (target instanceof Element && target.classList.contains('puffy-belt')) return;

      if (!held) {
        held = true;
        for (const belt of belts) belt.hold();
      }
      clearTimeout(timer);
      timer = setTimeout(release, SCROLL_QUIET);
    },
    { capture: true, passive: true }
  );

  return {
    add(belt) {
      belts.add(belt);
      if (held) belt.hold();
    },
  };
})();

function build(track) {
  const speed = Number(track.dataset.puffyAutoscrollSpeed) || DEFAULT_SPEED;
  const originals = [...track.children];
  if (!originals.length) return;

  const belt = document.createElement('div');
  belt.className = 'puffy-belt';
  track.parentNode.insertBefore(belt, track);
  belt.appendChild(track);

  // Seriile adăugate sunt decor, nu conținut. Cititoarele de ecran ar anunța
  // altfel fiecare recenzie de mai multe ori.
  const addSet = () => {
    for (const item of originals) {
      const copy = item.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      copy.dataset.puffyClone = '';
      for (const el of copy.querySelectorAll('a, button, input, video')) {
        el.setAttribute('tabindex', '-1');
      }
      track.appendChild(copy);
    }
  };

  addSet();

  /**
   * Lungimea unei serii, măsurată direct: distanța dintre primul card și prima
   * lui copie. Include spațiul dintre carduri exact o dată, care e exact ce
   * trebuie. Calculată din nou la fiecare redimensionare, pentru că lățimea
   * cardurilor se schimbă la fiecare prag de ecran.
   */
  let series = 0;

  const measure = () => {
    const first = originals[0];
    const clone = track.querySelector('[data-puffy-clone]');
    if (!first || !clone) return;
    series = clone.offsetLeft - first.offsetLeft;

    // După ce banda s-a mutat cu o serie, ce rămâne trebuie să acopere în
    // continuare tot cadrul, altfel apare o gaură la fiecare tur.
    let guard = 0;
    while (series > 0 && track.scrollWidth - series < belt.clientWidth + 4 && guard < 8) {
      addSet();
      series = track.querySelector('[data-puffy-clone]').offsetLeft - first.offsetLeft;
      guard += 1;
    }
  };

  measure();
  addEventListener('load', measure);
  if ('ResizeObserver' in window) new ResizeObserver(measure).observe(track);

  /* ------------------------------------------------------------ mișcarea -- */

  let holds = 0;
  let frame = 0;
  let last = 0;
  let resumeTimer = 0;

  const canRun = () => holds === 0 && !REDUCED.matches && series > 1;

  const step = (now) => {
    frame = 0;
    if (!canRun()) {
      last = 0;
      return;
    }
    if (!last) last = now;
    const dt = Math.min(now - last, 50);
    last = now;

    let next = belt.scrollLeft + speed * (dt / 1000);
    // Saltul înapoi cu o serie cade peste conținut identic, deci nu se vede.
    if (next >= series) next -= series;
    belt.scrollLeft = next;

    schedule();
  };

  const schedule = () => {
    if (frame || !canRun()) return;
    frame = requestAnimationFrame(step);
  };

  const stop = () => {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    last = 0;
  };

  const hold = () => { holds += 1; stop(); };
  const release = () => { holds = Math.max(0, holds - 1); if (!holds) schedule(); };

  // Mâna omului are prioritate. După ce termină de tras, banda repornește din
  // locul în care a lăsat-o el, nu de la început.
  const holdAWhile = () => {
    clearTimeout(resumeTimer);
    if (!resumeTimer) hold();
    resumeTimer = setTimeout(() => { resumeTimer = 0; release(); }, RESUME_DELAY);
  };

  belt.addEventListener('pointerenter', hold);
  belt.addEventListener('pointerleave', release);
  belt.addEventListener('focusin', hold);
  belt.addEventListener('focusout', release);
  belt.addEventListener('pointerdown', holdAWhile, { passive: true });
  belt.addEventListener('touchstart', holdAWhile, { passive: true });
  belt.addEventListener('touchmove', holdAWhile, { passive: true });
  belt.addEventListener('wheel', holdAWhile, { passive: true });

  // Oprită cât e în afara ecranului. `scrollLeft` scris la fiecare cadru
  // forțează recalcul de layout chiar și nevăzut.
  if ('IntersectionObserver' in window) {
    let away = true;
    hold();
    new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting === !away) continue;
          away = !entry.isIntersecting;
          if (away) hold();
          else release();
        }
      },
      { rootMargin: '80px 0px' }
    ).observe(belt);
  } else {
    schedule();
  }

  REDUCED.addEventListener?.('change', () => (REDUCED.matches ? stop() : schedule()));

  // Oprită cât alunecă pagina pe verticală.
  pageScroll.add({ hold, release });

  // Folosit de secțiunea de clipuri: banda stă pe loc cât rulează un clip cu
  // sunet, altfel cardul apăsat pleacă de sub deget.
  belt.puffyBelt = { hold, release };
  track.puffyAutoscroll = { hold, release };
}

function init() {
  for (const track of document.querySelectorAll('[data-puffy-autoscroll]')) {
    if (track.closest('.puffy-belt')) continue;
    build(track);
  }
}

/**
 * Pornit după ce pagina a randat. Construirea benzii clonează carduri și
 * măsoară lățimi, iar ambele carusele sunt mult sub primul ecran: nimic din ce
 * face codul ăsta nu e vizibil până când vizitatorul nu derulează mult.
 */
const later = () => {
  if ('requestIdleCallback' in window) requestIdleCallback(init, { timeout: 2000 });
  else setTimeout(init, 200);
};

if (document.readyState === 'complete') later();
else addEventListener('load', later, { once: true });

export {};
